import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Tab,
  Grid,
  Avatar,
  Stack,
  Paper,
  Button,
  IconButton,
  Dialog,
} from '@mui/material';
import { TabPanel, TabContext, TabList } from '@mui/lab';
import {
  AutoGraphOutlined,
  PeopleAlt,
  Public,
  Lock,
  Close,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Auth, API } from '../services/amplifyCompat';
import {
  getSingleGroupByName,
  getSingleGroup,
  getAllUsersInGroup,
  getAllOwnersInGroup,
} from '../graphql/queries';
import GroupMemberPanel from '../components/groupProfile/GroupMemberPanel';
import AddMemberPanel from '../components/groupProfile/AddMemberPanel';
import MemberActionsPanel from '../components/groupProfile/MemberActionsPanel';
import GroupInfoPanel from '../components/groupProfile/GroupInfoPanel';
import { styled } from '@mui/material/styles';
import GroupPageLeaderboard from '../components/groupProfile/GroupPageLeaderboard';
import EditGroupPanel from '../components/groupProfile/EditGroupPanel';
import { v4 as uuidv4 } from 'uuid';
import LeaveGroupDialogContent from '../components/groupProfile/LeaveGroupDialogContent';
import useTranslation from '../components/customHooks/translations';
import { useUserInfoContext } from '../hooks/use-user-info-context';
import { DisplayWeight } from '../components/DisplayWeight';

const StyledPaper = styled(Paper)`
  padding: 1em 2em;
  text-align: center;
  .statValue {
    margin-top: 0.5em;
  }
`;

const GroupProfile = () => {
  const translation = useTranslation();
  const { user } = useUserInfoContext();
  const { groupName } = useParams();
  // Stable tab ids so selection survives language switches
  const TAB = {
    memberActions: 'memberActions',
    groupInfo: 'groupInfo',
    groupMembers: 'groupMembers',
    addMembers: 'addMembers',
    editGroupInfo: 'editGroupInfo',
  };
  const [selectedTab, setSelectedTab] = useState(TAB.memberActions);
  const [groupInfo, setGroupInfo] = useState();
  const [groupMembers, setGroupMembers] = useState();
  const [groupOwners, setGroupOwners] = useState();
  // null = membership/ownership not yet resolved (avoids flashing Join Group for members)
  const [currentUserOwner, setCurrentUserOwner] = useState(null);
  const [currentUserMember, setCurrentUserMember] = useState(null);
  const [cognitoUser, setCognitoUser] = useState();
  const [userId, setUserId] = useState();
  const [groupId, setGroupId] = useState();
  const [leaveGroupWarning, setLeaveGroupWarning] = useState(false);
  const navigate = useNavigate();
  const groupLink =
    groupInfo &&
    '/group-profile/'.concat(
      encodeURIComponent(groupInfo.group_name),
      '/add/',
      uuidv4(),
      '-',
      groupId ** 2
    );

  useEffect(() => {
    const getGroupAndUserInfo = async () => {
      const [cognitoRes, groupInfoRes] = await Promise.all([
        Auth.currentAuthenticatedUser(),
        API.graphql({
          query: getSingleGroupByName,
          variables: { group_name: groupName },
        }),
      ]);
      setCognitoUser(cognitoRes);
      setGroupInfo(groupInfoRes.data.getSingleGroupByName);
      const currentUserId = Number(cognitoRes.attributes['custom:id']);
      setUserId(currentUserId);
      const currentGroupId = groupInfoRes.data.getSingleGroupByName.group_id;
      setGroupId(currentGroupId);
    };
    getGroupAndUserInfo();
  }, [groupName]);

  //gets list of all users, checks to see if current user is a group member
  const getGroupUsers = useCallback(async () => {
    const memberRes = await API.graphql({
      query: getAllUsersInGroup,
      variables: { group_id: groupId },
    });
    const members = memberRes.data.getAllUsersInGroup;
    setGroupMembers(members);
    const memberIds = members.map((member) => member.user_id);
    setCurrentUserMember(memberIds.includes(userId));
  }, [groupId, userId]);

  useEffect(() => {
    //gets array of all group owners and checks if the current user is a owner
    const isUserGroupOwner = async () => {
      const ownerRes = await API.graphql({
        query: getAllOwnersInGroup,
        variables: { group_id: groupId },
      });
      const owners = ownerRes.data.getAllOwnersInGroup;
      setGroupOwners(owners);
      const ownerIds = owners.map((owner) => owner.user_id);
      setCurrentUserOwner(ownerIds.includes(userId));
    };

    if (userId && groupId) {
      getGroupUsers();
      isUserGroupOwner();
    }
  }, [userId, groupId, getGroupUsers]);

  const handleTabChange = (e, newValue) => {
    setSelectedTab(newValue);
  };

  const getUpdatedGroup = async (id) => {
    const updatedGroupRes = await API.graphql({
      query: getSingleGroup,
      variables: { group_id: id },
    });
    setGroupInfo(updatedGroupRes.data.getSingleGroup);
  };

  return (
    <>
      {groupInfo && (
        <>
          <Grid
            container
            alignItems={{ xs: 'center' }}
            direction={{ xs: 'column', lg: 'row' }}
            sx={{ mt: '0' }}
            gap={{ xs: '2em', lg: '0' }}
            textAlign={{ xs: 'center', md: 'left' }}
          >
            <Grid
              container
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              alignItems={{ xs: 'center' }}
              spacing={{ xs: 4, sm: 8 }}
              sx={{ mb: '1.5em', overflow: 'auto' }}
            >
              <Grid item xs={2}>
                <Avatar
                  variant="rounded"
                  sx={{
                    background:
                      'linear-gradient(135deg, #0F766E 0%, #0D9488 45%, #14B8A6 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 10px 24px rgba(15,23,42,0.12)',
                    border: '1px solid #E2E8F0',
                    padding: '24px',
                    width: 150,
                    height: 150,
                  }}
                  src={groupInfo.group_image ? groupInfo.group_image : null}
                >
                  {groupInfo.group_name.charAt(0)}
                </Avatar>
              </Grid>
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '0.5em',
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    wordWrap: 'break-word',
                    maxWidth: { xs: '300px', sm: '100%' },
                  }}
                >
                  {groupName}
                </Typography>
                <Grid
                  container
                  sx={{
                    display: 'flex',
                    gap: '20px',
                    color: '#0D9488',
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <PeopleAlt />
                    <Typography component="div" variant="subtitle2">
                      {translation.membersColon}{' '}
                      {groupMembers && groupMembers.length}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={1}>
                    {groupInfo.is_public ? <Public /> : <Lock />}
                    <Typography component="div" variant="subtitle2">
                      {groupInfo.is_public
                        ? translation.public
                        : translation.private}
                    </Typography>
                  </Stack>
                </Grid>
                {currentUserMember === null ? null : currentUserMember ? (
                  <Button
                    variant="contained"
                    sx={{ mt: { xs: '1em' } }}
                    onClick={() => setLeaveGroupWarning(true)}
                  >
                    {translation.leaveGroup}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    sx={{ mt: { xs: '1em' } }}
                    onClick={() => {
                      navigate(groupLink);
                    }}
                  >
                    {translation.joinGroup}
                  </Button>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={7.5}
                justifyContent="center"
                sx={{ width: '70%' }}
              >
                <Box
                  component={Paper}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-evenly',
                    borderRadius: '10px',
                    background:
                      'linear-gradient(135deg, #0F766E 0%, #0D9488 45%, #14B8A6 100%)',
                    padding: '1em',
                    width: '100%',
                    border: 'none',
                    boxShadow: '0 6px 20px rgba(13,148,136,0.18)',
                    gap: { xs: '0.5em', md: '2' },
                  }}
                >
                  <StyledPaper
                    elevation={6}
                    sx={{ background: 'white', flex: '1' }}
                  >
                    <Typography variant="h4">
                      {translation.co2SavedWeek}
                    </Typography>
                    <Typography variant="h5" className="statValue">
                      <AutoGraphOutlined fontSize="large" />
                      <DisplayWeight weight={groupInfo.weekly_co2} unit="g" />
                    </Typography>
                  </StyledPaper>
                  <StyledPaper
                    elevation={6}
                    sx={{ background: 'white', flex: '1' }}
                  >
                    <Typography variant="h4">
                      {translation.totalCO2Saved}
                    </Typography>
                    <Typography variant="h5" className="statValue">
                      <DisplayWeight weight={groupInfo.total_co2} unit="g" />
                    </Typography>
                  </StyledPaper>
                </Box>
              </Grid>
            </Grid>

            <Grid
              container
              item
              sx={{
                mt: { xs: '2em', md: '3em' },
                width: { xs: '90%' },
              }}
            >
              <GroupPageLeaderboard
                currentGroup={groupInfo}
                groupMembers={groupMembers}
                userId={userId}
                user={user}
              />
            </Grid>
            <Grid
              container
              item
              sx={{
                mt: { xs: '2em', md: '3em' },
                width: { xs: '90%' },
              }}
            >
              <Typography variant="h2" paddingBottom={4}>
                {translation.groupInfo}
              </Typography>
              <TabContext value={selectedTab}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                    maxWidth: { xs: 320, sm: '100%' },
                    width: { sm: '100%' },
                    display: 'flex',
                    padding: '0.5em',
                  }}
                >
                  <TabList
                    onChange={handleTabChange}
                    aria-label="group profile tabs"
                    scrollButtons
                    allowScrollButtonsMobile
                    variant="scrollable"
                  >
                    <Tab label={translation.memberActions} value={TAB.memberActions} />
                    <Tab label={translation.groupInfo} value={TAB.groupInfo} />
                    <Tab label={translation.groupMembers} value={TAB.groupMembers} />
                    {/* only display following tabs if current user is a group owner */}
                    {currentUserOwner === true && (
                      <Tab label={translation.addMembers} value={TAB.addMembers} />
                    )}
                    {currentUserOwner === true && (
                      <Tab label={translation.editGroupInfo} value={TAB.editGroupInfo} />
                    )}
                  </TabList>
                </Box>
                <TabPanel
                  value={TAB.memberActions}
                  sx={{
                    padding: { xs: '0' },
                    width: '100%',
                  }}
                >
                  <MemberActionsPanel
                    groupInfo={groupInfo}
                    cognitoUser={cognitoUser}
                  />
                </TabPanel>
                <TabPanel
                  value={TAB.groupInfo}
                  sx={{
                    width: '100%',
                  }}
                >
                  <GroupInfoPanel
                    groupOwners={groupOwners}
                    groupInfo={groupInfo}
                  />
                </TabPanel>
                <TabPanel
                  value={TAB.groupMembers}
                  sx={{
                    padding: { xs: '0' },
                    width: '100%',
                  }}
                >
                  <GroupMemberPanel
                    groupMembers={groupMembers}
                    setGroupMembers={setGroupMembers}
                    groupInfo={groupInfo}
                    currentUserOwner={currentUserOwner}
                    cognitoUser={cognitoUser}
                  />
                </TabPanel>
                <TabPanel
                  value={TAB.addMembers}
                  sx={{
                    padding: { xs: '1.5em 0' },
                    width: '100%',
                  }}
                >
                  <AddMemberPanel groupInfo={groupInfo} refetchMembers={getGroupUsers} />
                </TabPanel>
                <TabPanel
                  value={TAB.editGroupInfo}
                  sx={{
                    padding: { xs: '1.5em 0' },
                    width: '100%',
                  }}
                >
                  <EditGroupPanel
                    groupInfo={groupInfo}
                    getUpdatedGroup={getUpdatedGroup}
                  />
                </TabPanel>
              </TabContext>
            </Grid>
          </Grid>
          {/* leave group warning dialog */}
          <Dialog
            aria-labelledby="leave-group-dialog"
            PaperProps={{
              sx: {
                p: '1em',
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                alignItems: 'center',
              },
            }}
            open={leaveGroupWarning}
            onClose={() => setLeaveGroupWarning(false)}
          >
            <IconButton
              aria-label="Close"
              sx={{ alignSelf: 'flex-end' }}
              onClick={() => setLeaveGroupWarning(false)}
            >
              <Close />
            </IconButton>
            <LeaveGroupDialogContent
              handleClose={() => setLeaveGroupWarning(false)}
              groupInfo={groupInfo}
              groupMembers={groupMembers}
              currentUserOwner={currentUserOwner}
              userId={userId}
              cognitoUser={cognitoUser}
            />
          </Dialog>
        </>
      )}
    </>
  );
};

export default GroupProfile;
